import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Mail, 
  Calendar, 
  Tag, 
  FileText, 
  Download,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const ResourceLeadsPanel = ({ leads, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  // Get unique categories from leads
  const categories = ['all', ...new Set(leads.map(lead => lead.category))];

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.resource_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || lead.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Export to CSV
  const exportToCSV = () => {
    try {
      const headers = ['Email', 'Resource', 'Category', 'Date'];
      const rows = filteredLeads.map(lead => [
        lead.email,
        lead.resource_name,
        lead.category.toUpperCase(),
        new Date(lead.submitted_at).toLocaleDateString()
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resource-leads-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      vce: '#006C69',
      ielts: '#006C69',
      selective: '#B7BF72',
      eal: '#B7BF72',
      gmat: '#006C69',
      gre: '#B7BF72',
      sat: '#006C69',
      'special-needs': '#B7BF72'
    };
    return colors[category.toLowerCase()] || '#6b7280';
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border-2" style={{ borderColor: 'rgba(0, 108, 105, 0.20)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0, 108, 105, 0.15)' }}>
              <Mail className="w-5 h-5" style={{ color: '#006C69' }} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{leads.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-2" style={{ borderColor: 'rgba(183, 191, 114, 0.20)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(183, 191, 114, 0.15)' }}>
              <Tag className="w-5 h-5" style={{ color: '#B7BF72' }} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-gray-900">{categories.length - 1}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-2" style={{ borderColor: 'rgba(0, 108, 105, 0.20)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0, 108, 105, 0.15)' }}>
              <FileText className="w-5 h-5" style={{ color: '#006C69' }} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Filtered</p>
              <p className="text-2xl font-bold text-gray-900">{filteredLeads.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by email or resource name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border rounded-lg bg-white text-sm font-medium"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Export Button */}
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="font-semibold"
            style={{ borderColor: '#006C69', color: '#006C69' }}
            disabled={filteredLeads.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </Card>

      {/* Leads Table */}
      {filteredLeads.length === 0 ? (
        <Card className="p-12 text-center">
          <Mail className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {searchQuery || filterCategory !== 'all' ? 'No Matching Leads' : 'No Leads Yet'}
          </h3>
          <p className="text-gray-600">
            {searchQuery || filterCategory !== 'all' 
              ? 'Try adjusting your filters'
              : 'Email leads will appear here when students request premium resources'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Date Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredLeads.map((lead, index) => (
                  <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{lead.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{lead.resource_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge
                        className="font-semibold"
                        style={{
                          background: getCategoryColor(lead.category),
                          color: '#FFFFFF'
                        }}
                      >
                        {lead.category.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{formatDate(lead.submitted_at)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ResourceLeadsPanel;
